import {
    FC,
    Fragment,
    type CSSProperties,
    ReactNode,
    useEffect,
    useMemo,
} from "react";
import Container from "../Container"
import classes from "./signUp.module.css"
import { REGISTRATION_STEPS } from "@/data";
import { cx } from "class-variance-authority";
import { Icons } from "@/assets";

const SignUpStepContainer : FC<{step: REGISTRATION_STEPS, productSteps: REGISTRATION_STEPS[] | undefined, header: string, description: (string | ReactNode)[], children?: ReactNode}> =
    ({step, productSteps, header, description, children}) => 
{
    useEffect(() => {
        document.body.scrollTop = 0; // For Safari
        document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
    }, [])
    
    return (
        <Container>
            <div className="min-h-screen flex flex-col items-center mt-6 px-4">
                <StepProgress currentStep={step} productSteps={productSteps}/>
                <div className="flex flex-col md:flex-row gap-[30px] md:gap-[175px]">
                    <h1 className="max-w-[600px] text-ink">
                        {header}
                    </h1>
                    <div className="max-w-[445px] text-[16px]/[24px] text-grey-400">
                    {description.map((desc, i) => (
                        <Fragment key={i}>
                            {desc}
                            <br />
                        </Fragment>
                    ))}
                    </div>
                </div>
                {children}
            </div>
        </Container>
    )
}

export default SignUpStepContainer;

const StepNamesMap: Record<typeof REGISTRATION_STEPS[keyof typeof REGISTRATION_STEPS], string> = {
  [REGISTRATION_STEPS.NONE]: "",
  [REGISTRATION_STEPS.ACCOUNT_CREATION]: "Paskyros patvirtinimas",
  [REGISTRATION_STEPS.QUESTIONAIRE]: "Klausimynas",
  [REGISTRATION_STEPS.MEDNET_REG]: "Registracija mėginių pridavimui",
  [REGISTRATION_STEPS.CALENDLY_REG]: "Konsultacija",
  [REGISTRATION_STEPS.VO2MAX_CALENDLY_REG]: "VO₂ max",
  [REGISTRATION_STEPS.DONE]: "Peržiūra",
};

const StepProgress: FC<{currentStep: REGISTRATION_STEPS, productSteps: REGISTRATION_STEPS[] | undefined}> = ({currentStep, productSteps}) => {

    const steps: REGISTRATION_STEPS[] = useMemo(() => {
        return [REGISTRATION_STEPS.ACCOUNT_CREATION, REGISTRATION_STEPS.QUESTIONAIRE, ...(productSteps || [])]
    }, [productSteps]);

    const currentStepIdx = steps.indexOf(currentStep);
    
    const progressStyle = {
        "--progress-steps": String(steps.length),
    } as CSSProperties;

    return (
        <div
            className={cx(
                classes.progress,
                steps.length % 2 === 1 && classes.progressOddSteps,
                "mt-[76px] mb-12",
            )}
            style={progressStyle}
        >
            {steps.map((step, index) => {
                if (!steps.includes(step)) return null;

                const isCompleted = index < currentStepIdx;
                const isActive = index === currentStepIdx;

                return (
                    <div className={cx(classes.step, isCompleted && classes.completed, isActive && classes.active)} key={step}>
                        <div className={classes.stepProgress}>
                            <div className={classes.line}/>
                            <div className={classes.circle}>
                                {isCompleted ? <Icons.Checkmark/> : index+1}
                            </div>
                            <div className={classes.line}/>
                        </div>
                        <div className={classes.label}>{StepNamesMap[step]}</div>
                    </div>
                );
            })}
        </div>
    );
}